import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router";
import {
  Badge,
  Button,
  Card,
  EmptyState,
  PageHeader,
  Spinner,
  Stat,
  Table,
  type BadgeVariant,
  type TableColumn,
} from "../ds";
import { FleetFlashModal } from "../components/FleetFlashModal";
import { api, errorText, type FlashSchedule } from "../lib/api";
import { fmtDateTime, relTime } from "../lib/format";

// „Geplante Updates": alle vom Server terminierten Firmware-Updates der ganzen
// Fleet — „was passiert heute Nacht?" in einer Ansicht. Gefahren werden sie vom
// Zeitplan-Thread im Server (core/schedule.py).
//
// Angelegt werden sie hier für eine ganze Welle (FleetFlashModal) oder einzeln
// auf der Router-Detailseite (System → Firmware) — beides über denselben
// Endpunkt, ein Termin je Router.

const STATUS_VARIANT: Record<FlashSchedule["status"], BadgeVariant> = {
  pending: "info",
  running: "warn",
  done: "success",
  error: "danger",
  canceled: "neutral",
};

const STATUS_LABEL: Record<FlashSchedule["status"], string> = {
  pending: "geplant",
  running: "läuft",
  done: "erledigt",
  error: "fehlgeschlagen",
  canceled: "abgesagt",
};

function scheduleColumns(
  onDeviceClick: (id: string) => void,
  busyId: string | null,
  onCancel: (entry: FlashSchedule) => void,
): TableColumn<FlashSchedule>[] {
  return [
    {
      key: "runAt",
      header: "Zeitpunkt",
      cell: (s) => (
        <span>
          {fmtDateTime(s.runAt)}
          <span className="block text-xs text-content-3">{relTime(s.runAt)}</span>
        </span>
      ),
    },
    {
      key: "device",
      header: "Router",
      cell: (s) => (
        <Button size="sm" variant="ghost" onClick={() => onDeviceClick(s.deviceId)}>
          {s.deviceHostname || s.deviceId}
        </Button>
      ),
    },
    {
      key: "firmware",
      header: "Image",
      cell: (s) => (
        <span className="flex flex-wrap items-center gap-2">
          {s.firmwareVersion}
          {s.firmwareTag && <Badge variant="info">{s.firmwareTag}</Badge>}
        </span>
      ),
    },
    {
      key: "status",
      header: "Status",
      cell: (s) => (
        <Badge variant={STATUS_VARIANT[s.status]} dot>
          {STATUS_LABEL[s.status]}
        </Badge>
      ),
    },
    { key: "trigger", header: "Geplant von", hideOnMobile: true, cell: (s) => s.trigger || "—" },
    {
      key: "error",
      header: "Hinweis",
      wrap: true,
      cell: (s) => <span className="text-xs text-danger-fg">{s.error}</span>,
    },
    {
      key: "actions",
      header: "",
      align: "right",
      className: "actions",
      // Absagen geht nur, solange nichts läuft — ein begonnener Flash hat kein
      // Zurück, und ein erledigter Termin ist Historie.
      cell: (s) =>
        s.status === "pending" ? (
          <Button
            size="sm"
            variant="ghost"
            iconName="close"
            loading={busyId === s.id}
            onClick={() => onCancel(s)}
          >
            Absagen
          </Button>
        ) : null,
    },
  ];
}

export function SchedulePage() {
  const navigate = useNavigate();
  const [rows, setRows] = useState<FlashSchedule[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [planning, setPlanning] = useState(false);

  const load = useCallback(() => {
    api
      .listFlashSchedules()
      .then((s) => {
        setRows(s);
        setError(null);
      })
      .catch((e: unknown) => {
        setRows([]);
        setError(errorText(e));
      });
  }, []);

  useEffect(load, [load]);

  async function cancel(entry: FlashSchedule) {
    setBusyId(entry.id);
    try {
      await api.cancelFlashSchedule(entry.id);
      load();
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusyId(null);
    }
  }

  // Ein Router mit offenem Termin nimmt keinen zweiten — der Dialog zeigt ihn
  // als „geplant" und lässt ihn nicht anhaken.
  const openEntries = rows?.filter((s) => s.status === "pending" || s.status === "running") ?? [];
  const openCount = rows?.filter((s) => s.status === "pending").length ?? 0;
  const next = rows?.find((s) => s.status === "pending");

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Geplante Updates"
        subtitle="Firmware-Updates mit Uhrzeit. Der Server fährt sie — dafür muss niemand angemeldet sein."
        actions={
          <>
            <Button variant="primary" iconName="timer" onClick={() => setPlanning(true)}>
              Flash planen
            </Button>
            <Button variant="outline" iconName="refresh" onClick={load}>
              Neu laden
            </Button>
          </>
        }
      />

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        <Stat label="Offen" value={openCount} iconName="timer" loading={rows === null} />
        <Stat
          label="Nächster"
          value={next ? relTime(next.runAt) : "—"}
          // Wann genau — „in 9 Stunden“ allein reicht nicht, wenn jemand
          // entscheiden muss, ob er den Termin noch abbläst.
          delta={next ? fmtDateTime(next.runAt) : undefined}
          deltaDir={null}
          iconName="upload"
          loading={rows === null}
        />
        <Stat
          label="Fehlgeschlagen"
          value={rows?.filter((s) => s.status === "error").length ?? 0}
          iconName="close"
          loading={rows === null}
        />
      </div>

      <Card title="Termine" subtitle="Offene zuerst, darunter die Historie." bodyPadding={false}>
        {rows === null ? (
          <div className="flex justify-center p-10">
            <Spinner />
          </div>
        ) : error ? (
          <EmptyState iconName="close" title="Termine nicht ladbar" description={error} />
        ) : (
          <Table
            columns={scheduleColumns(
              (id) => void navigate(`/fleet/${id}`),
              busyId,
              (entry) => void cancel(entry),
            )}
            rows={rows}
            rowKey={(s) => s.id}
            emptyMessage="Nichts geplant — „Flash planen“ oben, oder einzeln auf der Router-Seite unter „System → Firmware“."
          />
        )}
      </Card>

      {planning && (
        <FleetFlashModal
          plannedDeviceIds={new Set(openEntries.map((s) => s.deviceId))}
          onClose={() => setPlanning(false)}
          onDone={load}
        />
      )}
    </div>
  );
}
